import React, { useEffect, useRef } from "react";
import "trix/dist/trix";
import "trix/dist/trix.css";
import { TrixEditor } from "react-trix";
import ServiceWrapper from "Services/ServiceWrapper";
import AuthManager from "Common/AuthManager";
import { toast } from 'Components/DesignSystem/Toast/useToast';

type TrixEditorProps = {
  id: string;
  updateInfo: (value: string) => void;
  value?: string
  selectedItemId: string | undefined | null
  attachmentUrl: string
}

export const sanitizeUrl = (url: string): string => {
  const trimmed = url.trim();
  if (!trimmed) return '';

  // Block dangerous protocols
  if (/^(javascript|data|file|vbscript):/i.test(trimmed)) return '';

  // Accept full http / https URLs as-is
  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  // Promote protocol-relative URLs (//example.com → https://example.com)
  if (trimmed.startsWith('//')) return `https:${trimmed}`;

  // Auto-prepend https:// for bare URLs (www.example.com, example.com, …)
  return `https://${trimmed}`;
};

export const sanitizeHtmlLinks = (html: string): string => {
  if (!html || !html.includes('<a')) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('a[href]').forEach(anchor => {
    const href = anchor.getAttribute('href') ?? '';
    const safe = sanitizeUrl(href);
    if (!safe) anchor.removeAttribute('href');
    else if (safe !== href) anchor.setAttribute('href', safe);
  });
  return doc.body.innerHTML;
};

export default function TrixTextEditor({id, updateInfo, value, selectedItemId, attachmentUrl}: TrixEditorProps) {
  const editorRef = useRef<any>(null);
  const isInternalChange = useRef(false);
  // Set to true before programmatic loadHTML calls so handleChange can ignore
  // the trix-change event that Trix fires in response — it's not a user edit.
  const isProgrammaticLoad = useRef(false);
  const previousValue = useRef(value);

  useEffect(() => {
    // Skip if change was triggered internally (user typed → updateInfo → value prop update)
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }

    // Skip if value hasn't changed
    if (value === previousValue.current) {
      return;
    }

    // Programmatic load: flag BEFORE loadHTML so the resulting trix-change event
    // in handleChange is recognised as non-user input and skipped.
    if (editorRef.current) {
      previousValue.current = value;
      isProgrammaticLoad.current = true;
      editorRef.current.loadHTML(value || '');
    }
  }, [selectedItemId, value]);

  const handleEditorReady = (editor: any) => {
    editorRef.current = editor;
    if (value) {
      isProgrammaticLoad.current = true;
      editor.loadHTML(value);
    }
  };

  const handleChange = (html: string, _text: string) => {
    // Ignore trix-change events fired by programmatic loadHTML calls (field
    // selection, initial load). These are not user edits and must not trigger
    // the autosave chain → setUserProjectDataModels → upsert_data_models API.
    if (isProgrammaticLoad.current) {
      isProgrammaticLoad.current = false;
      return;
    }
    // react-trix passes e.target.innerHTML which includes Trix's mutable UI elements
    // (toolbar, caption editor clone) while an attachment is selected. Trix always
    // updates the connected hidden input with clean serialized HTML via its shadow
    // element BEFORE firing trix-change, so prefer that over the raw innerHTML.
    const inputEl = (editorRef.current?.element as any)?.inputElement as HTMLInputElement | null;
    const cleanHtml = inputEl?.value ?? html;
    const sanitized = sanitizeHtmlLinks(cleanHtml);
    if (sanitized !== previousValue.current) {
      isInternalChange.current = true;
      previousValue.current = sanitized;
      updateInfo(sanitized);
    }
  };

  let mergeTags = [
    {
      trigger: "@",
      tags: [
        { name: "Dominic St-Pierre", tag: "@dominic" },
        { name: "John Doe", tag: "@john" }
      ]
    },
    {
      trigger: "{",
      tags: [
        { name: "First name", tag: "{{ .FirstName }}" },
        { name: "Last name", tag: "{{ .LastName }}" }
      ]
    }
  ];


  // WebP conversion utility
  const convertWebPToBlob = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const convertedFile = new File([blob], file.name.replace('.webp', '.png'), {
              type: 'image/png'
            });
            resolve(convertedFile);
          } else {
            reject(new Error('Failed to convert WebP to PNG'));
          }
        }, 'image/png');
      };
      
      img.onerror = () => reject(new Error('Failed to load WebP image'));
      img.src = URL.createObjectURL(file);
    });
  };

  useEffect(() => {
    const handleTrixInit = (event: any) => {
      const toolbar = event.target?.toolbarElement as HTMLElement | null;
      if (!toolbar) return;

      // Run in the capture phase so we modify the input before Trix reads it
      toolbar.addEventListener('click', (e: MouseEvent) => {
        const btn = (e.target as Element)?.closest('[data-trix-method="setAttribute"]');
        if (!btn) return;

        const input = toolbar.querySelector<HTMLInputElement>('[data-trix-input][name="href"]');
        if (!input || !input.value) return;

        const sanitized = sanitizeUrl(input.value);
        if (!sanitized) {
          e.stopImmediatePropagation();
          e.preventDefault();
          toast.error('This URL type is not allowed');
        } else {
          input.value = sanitized;
        }
      }, true);
    };

    document.addEventListener('trix-initialize', handleTrixInit);
    return () => document.removeEventListener('trix-initialize', handleTrixInit);
  }, []);

  useEffect(() => {
    // Handle file attachment
    const handleFileAccept = async (event: any) => {
      const attachment = event.attachment;
      const file = attachment.file;
      if(!file) return
      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp'
      ];
      if (!allowedTypes.includes(file.type)) {
        event.preventDefault();
        toast.error('Only image files (JPEG, PNG, GIF, WEBP) are allowed');
        attachment.remove()
        return;
      }
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        event.preventDefault();
        toast.error('File size must be less than 10MB');
        attachment.remove()
        return;
      }

      // Create FormData
      const formData = new FormData();
      
      try {
        let fileToUpload = file;
        
        // Convert WebP to PNG if necessary
        if (file.type === 'image/webp') {
          fileToUpload = await convertWebPToBlob(file);
        }
        
        formData.append('file', fileToUpload);

        attachment.setAttributes({
          url: '',
          href: ''
        });
        // Custom upload function
        const response = await ServiceWrapper.doPost(
          attachmentUrl,
          formData,
          null,
          AuthManager.getGmsToken(),
          true
        );

        if (!response.data.done) {
          throw new Error(`Upload failed: ${response.message}`);
        }
        
        const img = new Image();
        img.onload = () => {
          // Set proper attributes for image attachment
          attachment.setAttributes({
            url: response.data.data,
            href: response.data.data,
            filename: file.name,
            filesize: file.size,
            contentType: 'image',
            width: img.width,
            height: img.height,
            caption: file.name
          });
        };
        img.onerror = () => {
          // Fallback to link if image fails to load
          attachment.setAttributes({
            url: response.data.data,
            href: response.data.data,
            filename: file.name,
            filesize: file.size,
            contentType: 'image',
            caption: file.name
          });
        };
        img.src = response.data.data;
      } catch (error) {
        console.error('Upload error:', error);
        // Handle failed upload
        attachment.remove();
      }
    };

    const handleImageLoad = async (event: any) => {
      const img = event.target;
      if (img.src.endsWith('.webp')) {
        try {
          const response = await fetch(img.src);
          const blob = await response.blob();
          const file = new File([blob], 'image.webp', { type: 'image/webp' });
          const convertedFile = await convertWebPToBlob(file);
          img.src = URL.createObjectURL(convertedFile);
        } catch (error) {
          console.error('Error converting WebP on load:', error);
        }
      }
    };

    // Named (not inline) so the cleanup below can remove the same function
    // reference - an inline arrow here would make removeEventListener a no-op,
    // leaking one document-level 'trix-initialize' listener per mount.
    const handleTrixInitializeForImageLoad = () => {
      const editor = editorRef.current?.element;
      if (editor) {
        editor.addEventListener('load', handleImageLoad, true);
      }
    };

    document.addEventListener("trix-attachment-add", handleFileAccept);
    document.addEventListener('trix-initialize', handleTrixInitializeForImageLoad);

    return () => {
      document.removeEventListener("trix-attachment-add", handleFileAccept);
      document.removeEventListener('trix-initialize', handleTrixInitializeForImageLoad);
      const editor = editorRef.current?.element;
      if (editor) {
        editor.removeEventListener('load', handleImageLoad, true);
      }
    };
  }, []);

  return (
    <div id={id}>
      <TrixEditor
        className="trix-editor"
        placeholder="Enter Info"
        value={value}
        mergeTags={mergeTags}
        onChange={handleChange}
        onEditorReady={handleEditorReady}
      />
      <div id={`${id}-info`} className="trix-editor-info">
        <small id={`${id}-formats-text`}>Supported image formats: JPEG, PNG, GIF, WebP (max 10MB)</small>
      </div>
    </div>
  );
}
